/**
 * Keeping one book on the device, and saying so.
 *
 * `lib/offline.ts` has been able to do this since the service worker landed;
 * nothing called it, so the feature existed only on paper. This is the surface:
 * one button in the reader's header that downloads the book being read, shows
 * what it costs, and gives it back.
 *
 * What gets downloaded is exactly the URL shapes the worker will serve from
 * the offline cache (`BOOK_ASSET_RE` in sw.ts): the book's record, every
 * chapter's text, the page map, the text-audio alignment, the document and
 * the narration. Anything else a reader needs offline is already in the
 * shell cache, which the worker precaches on install.
 *
 * The audio is by far the largest of them and the one that fails first on a
 * full device, so it is fetched last: a book whose text is on hand and whose
 * audio is not still reads, whereas the reverse is a book you cannot open.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { DownloadIcon, OfflineIcon, TrashIcon } from "../components/icons";
import { IconButton } from "../components/ui";
import { t, useUiLang } from "../i18n";
import {
  bookAssets,
  downloadBook,
  estimateUsage,
  listOffline,
  removeBook,
  supportsOffline,
  type StorageUsage,
} from "../lib/offline";
import type { BookDetail } from "../lib/types";

type State =
  | { name: "absent" }
  | { name: "downloading"; done: number; total: number }
  | { name: "saved"; bytes: number }
  | { name: "failed"; message: string };

/** Bytes as a reader reads them. Decimal units, because that is what a phone's
 * own storage screen shows and a second convention beside it only confuses. */
function size(bytes: number, lang: string): string {
  if (bytes <= 0) return "-";
  const units = ["kB", "MB", "GB"];
  let value = bytes / 1000;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const digits = value < 10 ? 1 : 0;
  return `${value.toLocaleString(lang, { maximumFractionDigits: digits })} ${units[unit]}`;
}

export function OfflineButton({ book }: { book: BookDetail }) {
  useUiLang();
  const lang = document.documentElement.lang || "en";
  const [state, setState] = useState<State>({ name: "absent" });
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState<StorageUsage | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const anchor = useRef<HTMLDivElement>(null);

  // What is already on the device, asked once per book. A download in flight
  // must not be overwritten by a late answer, hence the guard on `downloading`.
  useEffect(() => {
    let alive = true;
    void listOffline().then((books) => {
      if (!alive) return;
      const found = books.find((entry) => entry.bookId === book.id);
      setState((current) =>
        current.name === "downloading"
          ? current
          : found
            ? { name: "saved", bytes: found.bytes }
            : { name: "absent" },
      );
    });
    return () => {
      alive = false;
    };
  }, [book.id]);

  useEffect(() => {
    if (!open) return;
    void estimateUsage().then(setUsage);
    const away = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!panel.current?.contains(target) && !anchor.current?.contains(target)) setOpen(false);
    };
    const timer = window.setTimeout(() => document.addEventListener("mousedown", away), 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", away);
    };
  }, [open]);

  const start = useCallback(async () => {
    const assets = bookAssets(book);
    setState({ name: "downloading", done: 0, total: assets.length });
    try {
      await downloadBook(book.id, assets, (progress) => {
        setState({ name: "downloading", done: progress.done, total: progress.total });
      });
      const books = await listOffline();
      const found = books.find((entry) => entry.bookId === book.id);
      setState({ name: "saved", bytes: found?.bytes ?? 0 });
      setOpen(true);
    } catch (error) {
      // A refused download is nearly always a full device, and the reader can
      // do something about that only if we say so instead of failing silently.
      setState({ name: "failed", message: error instanceof Error ? error.message : String(error) });
      setOpen(true);
    }
  }, [book]);

  const drop = useCallback(async () => {
    await removeBook(book.id);
    setState({ name: "absent" });
    setOpen(false);
  }, [book.id]);

  // A browser without Cache Storage (an old Safari, a private window) would
  // show a button that can only ever fail. It shows nothing instead.
  if (!supportsOffline()) return null;

  const saved = state.name === "saved";
  const busy = state.name === "downloading";
  const percent = busy ? Math.round((state.done / Math.max(1, state.total)) * 100) : 0;

  return (
    <div className="relative" ref={anchor}>
      <IconButton
        label={saved ? t("offlineSavedLabel") : t("offlineKeepLabel")}
        aria-expanded={open}
        disabled={busy}
        onClick={() => {
          if (saved || state.name === "failed") setOpen((value) => !value);
          else void start();
        }}
        className={saved ? "text-[var(--color-accent)]" : undefined}
      >
        {busy ? (
          <span className="font-mono text-2xs tabular-nums">{percent}</span>
        ) : saved ? (
          <OfflineIcon />
        ) : (
          <DownloadIcon />
        )}
      </IconButton>

      {open && (
        <div
          ref={panel}
          role="dialog"
          aria-label={t("offlineTitle")}
          className={
            // Two anchorings, because one does not fit both widths. Hung off
            // the button's right edge, the panel spilled past the left of a
            // 390px screen and lost its corner: the button sits mid-header,
            // not at the edge, so there is no room to its left for 233px.
            // On a phone it is therefore pinned to the screen's own margins,
            // under the header; from sm up it goes back to being a popover
            // under the button it belongs to.
            "fixed inset-x-fib13 top-fib55 z-30 w-auto rounded-xl " +
            "sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-fib8 sm:w-fib233 " +
            "bg-[var(--color-raised)] p-4 shadow-2xl shadow-black/50 ring-1 ring-[var(--color-line)]"
          }
        >
          <h2 className="text-sm font-medium text-[var(--color-ink)]">{t("offlineTitle")}</h2>

          {state.name === "failed" ? (
            <p className="mt-2 text-xs leading-relaxed text-[var(--color-muted)]">
              {t("offlineFailed")}
            </p>
          ) : (
            <p className="mt-2 text-xs leading-relaxed text-[var(--color-muted)]">
              {t("offlineSavedHint", { size: size(saved ? state.bytes : 0, lang) })}
            </p>
          )}

          {usage && (
            <p className="mt-2 text-2xs text-[var(--color-faint)]">
              {t("offlineUsage", {
                used: size(usage.usageBytes, lang),
                free: size(Math.max(0, usage.quotaBytes - usage.usageBytes), lang),
              })}
            </p>
          )}

          <div className="mt-3 flex gap-2">
            {saved ? (
              <button
                onClick={() => void drop()}
                className={
                  "tap flex items-center gap-1.5 rounded-lg bg-[var(--color-surface)] px-3 py-1.5 " +
                  "text-xs text-[var(--color-muted)] transition-colors duration-150 hover:text-[var(--color-ink)]"
                }
              >
                <TrashIcon className="h-4 w-4" />
                {t("offlineRemove")}
              </button>
            ) : (
              <button
                onClick={() => void start()}
                className={
                  "tap flex items-center gap-1.5 rounded-lg bg-[var(--color-accent-soft)] px-3 py-1.5 " +
                  "text-xs text-[var(--color-accent)] transition-colors duration-150"
                }
              >
                <DownloadIcon className="h-4 w-4" />
                {t("offlineRetry")}
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="tap rounded-lg px-3 py-1.5 text-xs text-[var(--color-muted)] hover:text-[var(--color-ink)]"
            >
              {t("offlineClose")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
